import { CSSProperties, useEffect, useRef } from 'react';
import useWidthBreakpoint from '../../../../../../hooks/useWidthBreakpoint';
import { useResultData } from '../../context';
import { txColor } from '../../txHelpers';
import { tokens } from '../../tokens';
import { pick } from '../../responsive';
import ExpandedRailInfo from '../shared/ExpandedRailInfo';
import ChartStack, { RsiDescriptor } from '../charts/ChartStack';

interface Props {
  txId: number;
  isFirst?: boolean;
  onCollapse?: () => void;
  onToggleMode?: () => void;
  onPrev?: () => void;
  onNext?: () => void;
  prevId?: number | null;
  nextId?: number | null;
}

const ExpandedRow = ({
  txId,
  isFirst,
  onCollapse,
  onToggleMode,
  onPrev,
  onNext,
  prevId,
  nextId,
}: Props) => {
  const displayData = useResultData();
  const { breakpoint } = useWidthBreakpoint();
  const rootRef = useRef<HTMLDivElement | null>(null);

  // Smooth-scroll the expanded row into view — on first expand and on every
  // prev/next navigation (each mounts a fresh ExpandedRow).
  useEffect(() => {
    rootRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  }, []);

  const tx = displayData.deepByTxId[txId];
  if (!tx) return null;
  const accentColor = txColor(tx.type);

  // Match the closed row's top border exactly so expanding never nudges the
  // content down by a border width.
  const topBorder = isFirst ? '1px solid rgba(255,255,255,0.22)' : 'none';

  const padding = pick(breakpoint, {
    sm: '0',
    md: '0',
    lg: '0',
  });

  const container: CSSProperties = pick<CSSProperties>(breakpoint, {
    sm: {
      display: 'flex',
      flexDirection: 'column',
      borderTop: topBorder,
      borderBottom: '1px solid rgba(255,255,255,0.22)',
    },
    md: {
      display: 'flex',
      alignItems: 'flex-start',
      borderTop: topBorder,
      borderBottom: '1px solid rgba(255,255,255,0.22)',
    },
    lg: {
      display: 'flex',
      alignItems: 'flex-start',
      borderTop: topBorder,
      borderBottom: '1px solid rgba(255,255,255,0.22)',
    },
  });

  const accent: CSSProperties = pick<CSSProperties>(breakpoint, {
    sm: { height: 3, background: accentColor, flexShrink: 0 },
    md: {
      width: 3,
      background: accentColor,
      flexShrink: 0,
      alignSelf: 'stretch',
    },
    lg: {
      width: 3,
      background: accentColor,
      flexShrink: 0,
      alignSelf: 'stretch',
    },
  });

  const rail: CSSProperties = pick<CSSProperties>(breakpoint, {
    sm: {
      width: '100%',
      background: '#000',
      padding: '16px 18px 20px',
      display: 'flex',
      flexDirection: 'column',
      gap: 16,
      borderBottom: '1px solid rgba(255,255,255,0.08)',
    },
    md: {
      width: 360,
      flexShrink: 0,
      background: '#000',
      padding: '20px 28px 24px 26px',
      display: 'flex',
      flexDirection: 'column',
      gap: 20,
      borderRight: '1px solid rgba(255,255,255,0.08)',
    },
    lg: {
      width: 360,
      flexShrink: 0,
      background: '#000',
      padding: '20px 28px 24px 26px',
      display: 'flex',
      flexDirection: 'column',
      gap: 20,
      borderRight: '1px solid rgba(255,255,255,0.08)',
    },
  });

  const rightSide: CSSProperties = pick<CSSProperties>(breakpoint, {
    sm: { display: 'flex', flexDirection: 'column' },
    md: { flex: 1, display: 'flex', flexDirection: 'column' },
    lg: { flex: 1, display: 'flex', flexDirection: 'column' },
  });

  const chartHeight = pick(breakpoint, { sm: 220, md: 320, lg: 440 });

  // Only mount RSI panels when the result actually has indicator data.
  const chartRsis: RsiDescriptor[] = displayData.raw.indicatorConfig
    ? [
        {
          key: 'opposite',
          side: 'opposite',
          label: `OPPOSITE ORDER RSI · p:${displayData.indicatorConfig.opposite.parentPeriod} · threshold ${displayData.indicatorConfig.opposite.parentThreshold}`,
        },
        {
          key: 'same',
          side: 'same',
          label: `SAME ORDER RSI · p:${displayData.indicatorConfig.same.parentPeriod} · threshold ${displayData.indicatorConfig.same.parentThreshold}`,
        },
      ]
    : [];

  return (
    <div
      ref={rootRef}
      style={{
        background: tokens.backgroundBase,
        padding,
        color: tokens.textPrimary,
        fontFamily: tokens.fontSans,
        maxWidth: 1400,
        margin: '0 auto',
        scrollMarginTop: 24,
      }}
    >
      <div style={container}>
        <div style={accent} />

        <div style={rail}>
          <ExpandedRailInfo tx={tx} onCollapse={onCollapse} />

          {onToggleMode && (
            <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
              <button
                type="button"
                onClick={onToggleMode}
                style={{
                  padding: '6px 12px',
                  background: 'transparent',
                  color: tokens.textMuted,
                  border: '1px solid rgba(255,255,255,0.18)',
                  borderRadius: 3,
                  fontFamily: tokens.fontMono,
                  fontSize: 10,
                  fontWeight: 600,
                  letterSpacing: '0.12em',
                  cursor: 'pointer',
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: 7,
                }}
              >
                <span
                  style={{
                    width: 6,
                    height: 6,
                    borderRadius: '50%',
                    background: tokens.textFaint,
                  }}
                />
                MRC DEEP DIVE · OFF
              </button>
            </div>
          )}

          <div
            style={{
              marginTop: 'auto',
              paddingTop: 16,
              borderTop: '1px solid rgba(255,255,255,0.1)',
              display: 'flex',
              gap: 8,
            }}
          >
            <button
              type="button"
              onClick={onNext}
              disabled={!onNext}
              style={{
                flex: 1,
                padding: '10px 14px',
                background: 'rgba(255,255,255,0.04)',
                border: 'none',
                color: onNext ? tokens.textSecondary : tokens.textFaint,
                fontFamily: tokens.fontMono,
                fontSize: 11,
                letterSpacing: '0.1em',
                cursor: onNext ? 'pointer' : 'default',
              }}
            >
              ↑ NEXT{' '}
              {nextId !== null && nextId !== undefined ? `#${nextId}` : ''}
            </button>
            <button
              type="button"
              onClick={onPrev}
              disabled={!onPrev}
              style={{
                flex: 1,
                padding: '10px 14px',
                background: 'rgba(255,255,255,0.04)',
                border: 'none',
                color: onPrev ? tokens.textSecondary : tokens.textFaint,
                fontFamily: tokens.fontMono,
                fontSize: 11,
                letterSpacing: '0.1em',
                cursor: onPrev ? 'pointer' : 'default',
              }}
            >
              ↓ PREV{' '}
              {prevId !== null && prevId !== undefined ? `#${prevId}` : ''}
            </button>
          </div>
        </div>

        <div style={rightSide}>
          <div style={{ position: 'relative' }}>
            <ChartStack txId={txId} mainHeight={chartHeight} rsis={chartRsis} />
          </div>
        </div>
      </div>
    </div>
  );
};

export default ExpandedRow;
